import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { ProjectService } from '../../../services/project.service';
import { GroupService } from '../../../services/group.service';
import { CommonModule } from '@angular/common';
import { BaseModalComponent } from '../base-modal/base-modal.component';
import { Group } from '../../../dtos/group';
import { BaseAlertComponent } from '../../alerts/base-alert/base-alert.component';

@Component({
  selector: 'app-project-create-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, BaseModalComponent, BaseAlertComponent],
  templateUrl: './project-create-modal.component.html',
  styleUrls: ['./project-create-modal.component.scss'],
})
export class ProjectCreateModalComponent implements OnInit, OnChanges {
  @Input() open = false;
  @Input() preselectedGroupId: string | null = null;
  @Output() openChange = new EventEmitter<boolean>();
  @Output() created = new EventEmitter<any>();

  projectForm: UntypedFormGroup;
  submitted = false;
  error: any = '';
  groups: Group[] = [];

  constructor(
    private fb: UntypedFormBuilder,
    private projectService: ProjectService,
    private groupService: GroupService,
  ) {
    this.projectForm = this.fb.group({
      groupId: ['', [Validators.required]],
      title: ['', [Validators.required, Validators.maxLength(255)]],
      description: ['', [Validators.maxLength(1000)]],
    });
  }

  ngOnInit(): void {
    this.syncGroupState();
    this.loadGroups();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open'] && this.open && !changes['open'].firstChange) {
      this.syncGroupState();
    }
    if (changes['preselectedGroupId'] && !changes['preselectedGroupId'].firstChange) {
      this.syncGroupState();
    }
  }

  private syncGroupState(): void {
    const control = this.projectForm.get('groupId');
    if (control) {
      if (this.preselectedGroupId) {
        control.setValue(this.preselectedGroupId);
        control.disable({ emitEvent: false });
      } else {
        control.enable({ emitEvent: false });
      }
    }
  }

  private loadGroups(): void {
    this.groupService.getGroups().subscribe({
      next: (groups) => {
        this.groups = groups;
        if (this.preselectedGroupId) {
          this.projectForm.get('groupId')?.setValue(this.preselectedGroupId);
        }
      },
      error: (err) => {
        console.error('Error loading groups:', err);
      },
    });
  }

  close() {
    this.open = false;
    this.submitted = false;
    this.error = '';
    this.projectForm.reset();
    this.openChange.emit(false);
  }

  submit() {
    this.submitted = true;
    this.error = '';
    if (!this.projectForm.valid) {
      return;
    }

    const payload = {
      groupId: this.projectForm.get('groupId')?.value,
      title: this.projectForm.get('title')?.value,
      description: this.projectForm.get('description')?.value,
    };

    this.projectService.createProject(payload.groupId, payload).subscribe({
      next: (res) => {
        this.created.emit(res);
        this.projectForm.reset();
        this.submitted = false;
        this.close();
      },
      error: (err) => {
        console.error('Could not create project', err);
        const detail =
          err?.error?.detail || err?.error?.message || err?.message || 'Could not create project.';
        this.error = { detail };
      },
    });
  }
}
